import React, { createContext, useState, useEffect, useContext } from 'react';
import { getCurrentUser, fetchUserAttributes, signOut, fetchAuthSession } from '@aws-amplify/auth';
import axios from 'axios';

// Use environment variable for API Gateway URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

console.log(process.env.REACT_APP_API_BASE_URL);

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null); // Cognito User Sub (immutable UUID)
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [totalQuizzesTaken, setTotalQuizzesTaken] = useState(0);
  const [lastQuizScore, setLastQuizScore] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        await getCurrentUser();
        const attributes = await fetchUserAttributes();
        const email = attributes.email;
        const sub = attributes.sub; // Cognito User Sub (immutable UUID)

        setIsAuthenticated(true);
        setUserEmail(email);
        setUserId(sub);
        setIsSubscribed(false); // Default to false initially

        // Fetch Cognito ID token for API Gateway authorization (Cognito authorizer validates ID tokens)
        try {
          const authSession = await fetchAuthSession();
          const idToken = authSession.tokens?.idToken?.toString();

          const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000 // 10 second timeout
          });

          setIsSubscribed(data.isSubscribed);
          setTotalQuizzesTaken(data.totalQuizzesTaken || 0);
          setLastQuizScore(data.lastQuizScore);
        } catch (subscriptionErr) {
          // Don't set error - just default to false so UI still works
          setIsSubscribed(false);
        }
      } catch (err) {
        if (err.name === 'UserUnAuthenticatedException' || err.name === 'NotAuthorizedException') {
          setIsAuthenticated(false);
          setUserEmail(null);
          setUserId(null);
          setIsSubscribed(false);
        } else {
          console.error('AuthContext: Auth check error:', err);
          setError(err.message || 'Failed to check authentication');
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUserEmail(null);
      setUserId(null);
      setIsSubscribed(false);
      setTotalQuizzesTaken(0);
      setLastQuizScore(null);
      setError(null);
      window.location.href = '/SignIn';
    } catch (err) {
      console.error('AuthContext: Sign-out error:', err);
      setError(err.message || 'Failed to sign out');
    }
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center font-quicksand">Error: {error}</div>;
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center font-quicksand">Loading authentication...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        userEmail,
        setUserEmail,
        userId,
        setUserId,
        isSubscribed,
        setIsSubscribed,
        totalQuizzesTaken,
        setTotalQuizzesTaken,
        lastQuizScore,
        setLastQuizScore,
        signOut: handleSignOut,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

